import React from 'react';
import './FormattedChatMessage.css';

type MessageBlock =
  | { type: 'paragraph'; lines: string[] }
  | { type: 'list'; items: string[] };

const normalizeAgentText = (text: string): string =>
  text.replace(/\\n/g, '\n').trim();

const parseMessageBlocks = (text: string): MessageBlock[] => {
  const lines = normalizeAgentText(text).split('\n');
  const blocks: MessageBlock[] = [];
  let paragraphLines: string[] = [];
  let listItems: string[] = [];

  const flushParagraph = () => {
    if (paragraphLines.length > 0) {
      blocks.push({ type: 'paragraph', lines: [...paragraphLines] });
      paragraphLines = [];
    }
  };

  const flushList = () => {
    if (listItems.length > 0) {
      blocks.push({ type: 'list', items: [...listItems] });
      listItems = [];
    }
  };

  lines.forEach((line) => {
    const trimmed = line.trim();

    if (!trimmed) {
      flushList();
      flushParagraph();
      return;
    }

    if (trimmed.startsWith('- ')) {
      flushParagraph();
      listItems.push(trimmed.slice(2).trim());
      return;
    }

    flushList();
    paragraphLines.push(trimmed);
  });

  flushList();
  flushParagraph();

  if (blocks.length === 0 && text.trim()) {
    blocks.push({ type: 'paragraph', lines: [text.trim()] });
  }

  return blocks;
};

const renderInlineFormatting = (text: string): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  const pattern = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match = pattern.exec(text);
  let key = 0;

  while (match) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    parts.push(<strong key={`strong-${key}`}>{match[1]}</strong>);
    key += 1;
    lastIndex = pattern.lastIndex;
    match = pattern.exec(text);
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
};

interface FormattedChatMessageProps {
  text: string;
  className?: string;
}

const FormattedChatMessage: React.FC<FormattedChatMessageProps> = ({ text, className }) => {
  const blocks = parseMessageBlocks(text);

  return (
    <div className={`v2-formatted-message ${className || ''}`.trim()}>
      {blocks.map((block, blockIndex) => {
        if (block.type === 'list') {
          return (
            <ul key={`list-${blockIndex}`} className="v2-formatted-message__list">
              {block.items.map((item, itemIndex) => (
                <li key={`item-${blockIndex}-${itemIndex}`}>
                  {renderInlineFormatting(item)}
                </li>
              ))}
            </ul>
          );
        }

        return (
          <p key={`paragraph-${blockIndex}`} className="v2-formatted-message__paragraph">
            {block.lines.map((line, lineIndex) => (
              <React.Fragment key={`line-${blockIndex}-${lineIndex}`}>
                {lineIndex > 0 && <br />}
                {renderInlineFormatting(line)}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
};

export default FormattedChatMessage;
